import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { RichTextEditor } from "@/components/RichTextEditor";
import { Star, StarOff, ArrowLeft, Plus, Upload, Calendar, Edit, Trash2, HelpCircle } from "lucide-react";
import { AuditLogger } from "@/utils/auditLogger";
import AuditLogsView from "@/components/AuditLogsView";

interface Story {
  id: string;
  title: string;
  excerpt: string;
  category: string;
  read_time: string;
  published_at: string;
  featured: boolean;
  slug: string;
  content?: string;
  image_url?: string;
}

interface QnAEntry {
  id?: string;
  story_id?: string;
  question: string;
  answer: string;
  sort_order: number;
}

const Admin = () => {
  const [articles, setArticles] = useState<Story[]>([]);
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [creating, setCreating] = useState(false);
  const [editingArticle, setEditingArticle] = useState<Story | null>(null);
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    excerpt: '',
    category: '',
    content: '',
    published_at: new Date().toISOString().split('T')[0],
    read_time: ''
  });
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [keepExistingImage, setKeepExistingImage] = useState(true);
  const [qnaEntries, setQnaEntries] = useState<QnAEntry[]>([]);

  const isEditing = !!editingArticle;
  const isFormOpen = showCreateForm || isEditing;

  const openCreateForm = () => {
    setFormData({
      title: '',
      slug: '',
      excerpt: '',
      category: '',
      content: '',
      published_at: new Date().toISOString().split('T')[0],
      read_time: ''
    });
    setImageFile(null);
    setKeepExistingImage(true);
    setEditingArticle(null);
    setQnaEntries([]);
    setShowCreateForm(true);
  };

  const openEditForm = async (article: Story) => {
    setFormData({
      title: article.title,
      slug: article.slug,
      excerpt: article.excerpt,
      category: article.category,
      content: article.content || '',
      published_at: new Date(article.published_at).toISOString().split('T')[0],
      read_time: article.read_time
    });
    setImageFile(null);
    setKeepExistingImage(true);
    setEditingArticle(article);
    setShowCreateForm(false);
    
    // Fetch existing Q&A entries
    await fetchQnaEntries(article.id);
  };

  const closeForm = () => {
    setShowCreateForm(false);
    setEditingArticle(null);
    setImageFile(null);
    setKeepExistingImage(true);
    setQnaEntries([]);
  };

  const generateSlug = (title: string) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value };
      // Auto-generate slug when title changes
      if (field === 'title') {
        updated.slug = generateSlug(value);
      }
      return updated;
    });
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast({
          title: "Invalid file",
          description: "Please select an image file.",
          variant: "destructive",
        });
        return;
      }
      
      // Validate file size (1MB limit for better performance)
      if (file.size > 1 * 1024 * 1024) {
        toast({
          title: "File too large",
          description: "Please select an image smaller than 1MB for optimal performance.",
          variant: "destructive",
        });
        return;
      }

      // Recommend WebP format
      if (!file.type.includes('webp')) {
        toast({
          title: "Format recommendation",
          description: "For best performance, consider using WebP format. Current file will be processed.",
        });
      }

      // Validate dimensions using FileReader and Image
      const img = new Image();
      const reader = new FileReader();
      
      reader.onload = (e) => {
        img.onload = () => {
          // Check if dimensions are close to 16:9 ratio (1200x630 is ideal)
          const aspectRatio = img.width / img.height;
          const targetRatio = 16 / 9;
          const tolerance = 0.2; // Allow some flexibility
          
          if (Math.abs(aspectRatio - targetRatio) > tolerance) {
            toast({
              title: "Image dimensions",
              description: `For best results, use images with 16:9 aspect ratio (like 1200x630px). Current: ${img.width}x${img.height}px`,
            });
          }
          
          if (img.width > 1920 || img.height > 1080) {
            toast({
              title: "Large image",
              description: "Large images may slow down your site. Consider resizing to 1200x630px for optimal performance.",
            });
          }
          
      setImageFile(file);
      setKeepExistingImage(false);
      
      // Log image upload
      AuditLogger.logImageUploaded(file.name, file.size);
        };
        img.src = e.target?.result as string;
      };
      
      reader.readAsDataURL(file);
    }
  };

  const uploadImage = async (file: File): Promise<string | null> => {
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
      const filePath = `articles/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('article-images')
        .upload(filePath, file);

      if (uploadError) {
        console.error('Upload error:', uploadError);
        return null;
      }

      const { data: { publicUrl } } = supabase.storage
        .from('article-images')
        .getPublicUrl(filePath);

      return publicUrl;
    } catch (error) {
      console.error('Error uploading image:', error);
      return null;
    }
  };

  const fetchQnaEntries = async (storyId: string) => {
    try {
      const { data, error } = await supabase
        .from('article_qna')
        .select('*')
        .eq('story_id', storyId)
        .order('sort_order', { ascending: true });

      if (error) {
        console.error('Error fetching Q&A entries:', error);
        return;
      }

      setQnaEntries(data || []);
    } catch (error) {
      console.error('Error fetching Q&A entries:', error);
    }
  };

  const addQnaEntry = () => {
    const newEntry: QnAEntry = {
      question: '',
      answer: '',
      sort_order: qnaEntries.length
    };
    setQnaEntries([...qnaEntries, newEntry]);
  };

  const updateQnaEntry = (index: number, field: keyof QnAEntry, value: string | number) => {
    const updated = [...qnaEntries];
    updated[index] = { ...updated[index], [field]: value };
    setQnaEntries(updated);
  };

  const removeQnaEntry = (index: number) => {
    const updated = qnaEntries.filter((_, i) => i !== index);
    // Update sort_order for remaining entries
    updated.forEach((entry, i) => {
      entry.sort_order = i;
    });
    setQnaEntries(updated);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation
    if (!formData.title.trim() || !formData.excerpt.trim() || !formData.category) {
      toast({
        title: "Missing fields",
        description: "Please fill in all required fields.",
        variant: "destructive",
      });
      return;
    }

    setCreating(true);
    
    try {
      let imageUrl = editingArticle?.image_url || null;
      
      // Upload new image if provided
      if (imageFile && !keepExistingImage) {
        const uploadedUrl = await uploadImage(imageFile);
        if (!uploadedUrl) {
          toast({
            title: "Image upload failed",
            description: "Failed to upload the cover image. Please try again.",
            variant: "destructive",
          });
          setCreating(false);
          return;
        }
        imageUrl = uploadedUrl;
      }

      const articleData = {
        title: formData.title.trim(),
        slug: formData.slug.trim() || generateSlug(formData.title),
        excerpt: formData.excerpt.trim(),
        category: formData.category,
        content: formData.content.trim() || null,
        published_at: formData.published_at + 'T00:00:00Z',
        read_time: formData.read_time.trim() || '5 min read',
        image_url: imageUrl
      };

      let storyId: string;

      if (isEditing) {
        // Update existing article
        const { error } = await supabase
          .from('stories')
          .update(articleData)
          .eq('id', editingArticle.id);

        if (error) {
          console.error('Error updating article:', error);
          toast({
            title: "Error",
            description: "Failed to update article. Please try again.",
            variant: "destructive",
          });
          return;
        }

        storyId = editingArticle.id;
        
        // Log article update
        const changes = Object.keys(articleData).reduce((acc, key) => {
          if (articleData[key] !== editingArticle[key]) {
            acc[key] = { from: editingArticle[key], to: articleData[key] };
          }
          return acc;
        }, {});
        
        await AuditLogger.logArticleUpdated(editingArticle.id, articleData.title, changes);
        
        toast({
          title: "Success",
          description: "Article updated successfully!",
        });
      } else {
        // Create new article
        const { data, error } = await supabase
          .from('stories')
          .insert([{ ...articleData, featured: false }])
          .select('id')
          .single();

        if (error) {
          console.error('Error creating article:', error);
          toast({
            title: "Error",
            description: "Failed to create article. Please try again.",
            variant: "destructive",
          });
          return;
        }

        storyId = data.id;
        
        // Log article creation
        await AuditLogger.logArticleCreated(data.id, articleData.title);
        
        toast({
          title: "Success",
          description: "Article created successfully!",
        });
      }

      // Handle Q&A entries
      if (qnaEntries.length > 0) {
        // Delete existing Q&A entries if editing
        if (isEditing) {
          await supabase
            .from('article_qna')
            .delete()
            .eq('story_id', storyId);
        }

        // Insert new Q&A entries
        const validQnaEntries = qnaEntries.filter(entry => 
          entry.question.trim() && entry.answer.trim()
        );

        if (validQnaEntries.length > 0) {
          const qnaData = validQnaEntries.map((entry, index) => ({
            story_id: storyId,
            question: entry.question.trim(),
            answer: entry.answer.trim(),
            sort_order: index
          }));

          const { error: qnaError } = await supabase
            .from('article_qna')
            .insert(qnaData);

          if (qnaError) {
            console.error('Error saving Q&A entries:', qnaError);
            toast({
              title: "Warning",
              description: "Article saved but failed to save Q&A entries.",
              variant: "destructive",
            });
          }
        }
      }

      // Reset form and close
      closeForm();
      
      // Refresh articles list
      await fetchArticles();

    } catch (error) {
      console.error('Error saving article:', error);
      toast({
        title: "Error",
        description: "An unexpected error occurred.",
        variant: "destructive",
      });
    } finally {
      setCreating(false);
    }
  };

  useEffect(() => {
    fetchArticles();
  }, []);

  useEffect(() => {
    // Check if we should open editor for a specific article
    const editId = searchParams.get('edit');
    if (editId && articles.length > 0) {
      const articleToEdit = articles.find(article => article.id === editId);
      if (articleToEdit) {
        openEditForm(articleToEdit);
      }
    }
  }, [articles, searchParams]);

  const fetchArticles = async () => {
    try {
      const { data, error } = await supabase
        .from('stories')
        .select('*')
        .order('published_at', { ascending: false });

      if (error) {
        console.error('Error fetching articles:', error);
        toast({
          title: "Error",
          description: "Failed to load articles.",
          variant: "destructive",
        });
        return;
      }

      setArticles(data || []);
    } catch (error) {
      console.error('Error fetching articles:', error);
      toast({
        title: "Error",
        description: "Failed to load articles.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const toggleFeatured = async (articleId: string, currentFeatured: boolean) => {
    setUpdatingId(articleId);
    try {
      // If setting as featured, first remove featured status from all other articles
      if (!currentFeatured) {
        await supabase
          .from('stories')
          .update({ featured: false })
          .neq('id', articleId);
      }

      // Then update the selected article
      const { error } = await supabase
        .from('stories')
        .update({ featured: !currentFeatured })
        .eq('id', articleId);

      if (error) {
        console.error('Error updating featured status:', error);
        toast({
          title: "Error",
          description: "Failed to update featured status.",
          variant: "destructive",
        });
        return;
      }

      // Refresh the articles list
      await fetchArticles();
      
      // Log featured status change
      const articleTitle = articles.find(a => a.id === articleId)?.title || 'Unknown Article';
      await AuditLogger.logArticleFeatured(articleId, articleTitle, !currentFeatured);
      
      toast({
        title: "Success",
        description: currentFeatured 
          ? "Article removed from featured." 
          : "Article set as featured.",
      });
    } catch (error) {
      console.error('Error updating featured status:', error);
      toast({
        title: "Error",
        description: "Failed to update featured status.",
        variant: "destructive",
      });
    } finally {
      setUpdatingId(null);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const featuredArticle = articles.find(article => article.featured);

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b border-border bg-background sticky top-0 z-50">
        <div className="container py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => window.history.back()}
                className="flex items-center gap-2"
              >
                <ArrowLeft className="h-4 w-4" />
                Back
              </Button>
              <h1 className="text-2xl font-bold">Admin Panel</h1>
            </div>
            <div className="flex items-center gap-2">
              {isEditing ? (
                <>
                  <Button
                    variant="outline"
                    asChild
                    className="flex items-center gap-2"
                  >
                    <a href={`/article/${editingArticle.slug}`} target="_blank" rel="noopener noreferrer">
                      View Article
                    </a>
                  </Button>
                  <Button
                    type="submit"
                    form="article-form"
                    disabled={creating}
                    className="flex items-center gap-2"
                  >
                    <Upload className="h-4 w-4" />
                    {creating ? "Updating..." : "Update Article"}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={closeForm}
                    className="flex items-center gap-2"
                  >
                    Cancel
                  </Button>
                </>
              ) : (
                <Button
                  onClick={openCreateForm}
                  className="flex items-center gap-2"
                  variant={showCreateForm ? "outline" : "default"}
                >
                  <Plus className="h-4 w-4" />
                  {showCreateForm ? "Cancel" : "New Article"}
                </Button>
              )}
              <Badge variant="outline">Manage Content</Badge>
            </div>
          </div>
        </div>
      </div>

      <div className="container py-8">
        {/* Create/Edit Article Form */}
        {isFormOpen && (
          <div className="mb-8">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Plus className="h-5 w-5" />
                  {isEditing ? 'Edit Article' : 'Create New Article'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form id="article-form" onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    {/* Title */}
                    <div className="space-y-2">
                      <Label htmlFor="title">Title *</Label>
                      <Input
                        id="title"
                        value={formData.title}
                        onChange={(e) => handleInputChange('title', e.target.value)}
                        placeholder="Enter article title"
                        required
                      />
                    </div>

                    {/* Slug */}
                    <div className="space-y-2">
                      <Label htmlFor="slug">Slug *</Label>
                      <Input
                        id="slug"
                        value={formData.slug}
                        onChange={(e) => handleInputChange('slug', e.target.value)}
                        placeholder="article-slug"
                        required
                      />
                    </div>

                    {/* Category */}
                    <div className="space-y-2">
                      <Label htmlFor="category">Category *</Label>
                      <Select value={formData.category} onValueChange={(value) => handleInputChange('category', value)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select category" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="New Music">New Music</SelectItem>
                          <SelectItem value="Industry News">Industry News</SelectItem>
                          <SelectItem value="New Artist Spotlight">New Artist Spotlight</SelectItem>
                          <SelectItem value="Opinion">Opinion</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Read Time */}
                    <div className="space-y-2">
                      <Label htmlFor="read_time">Read Time</Label>
                      <Input
                        id="read_time"
                        value={formData.read_time}
                        onChange={(e) => handleInputChange('read_time', e.target.value)}
                        placeholder="5 min read"
                      />
                    </div>

                    {/* Published Date */}
                    <div className="space-y-2">
                      <Label htmlFor="published_at">Published Date</Label>
                      <div className="relative">
                        <Calendar className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        <Input
                          id="published_at"
                          type="date"
                          value={formData.published_at}
                          onChange={(e) => handleInputChange('published_at', e.target.value)}
                          className="pl-10"
                        />
                      </div>
                    </div>

                    {/* Cover Image */}
                    <div className="space-y-2">
                      <Label htmlFor="image">Cover Image</Label>
                      <div className="relative">
                        <Upload className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        <Input
                          id="image"
                          type="file"
                          accept="image/*"
                          onChange={handleImageChange}
                          className="pl-10"
                        />
                      </div>
                      {imageFile && (
                        <p className="text-sm text-muted-foreground">
                          Selected: {imageFile.name}
                        </p>
                      )}
                      {isEditing && editingArticle?.image_url && keepExistingImage && (
                        <div className="mt-2">
                          <p className="text-sm text-muted-foreground mb-2">
                            Current image: {editingArticle.image_url.split('/').pop()}
                          </p>
                          <img 
                            src={editingArticle.image_url} 
                            alt="Current cover" 
                            className="w-20 h-20 object-cover rounded border"
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Excerpt */}
                  <div className="space-y-2">
                    <Label htmlFor="excerpt">Excerpt *</Label>
                    <Textarea
                      id="excerpt"
                      value={formData.excerpt}
                      onChange={(e) => handleInputChange('excerpt', e.target.value)}
                      placeholder="Brief description of the article..."
                      rows={3}
                      required
                    />
                  </div>

                  {/* Content */}
                  <div className="space-y-2">
                    <Label htmlFor="content">Content</Label>
                    <RichTextEditor
                      content={formData.content}
                      onChange={(content) => handleInputChange('content', content)}
                      placeholder="Write your article content here..."
                    />
                    <p className="text-sm text-muted-foreground">
                      Use the toolbar to format your content with headings, lists, links, and more.
                    </p>
                  </div>

                  {/* Q&A Section */}
                  <div className="space-y-4">
                    <Label className="text-base font-semibold flex items-center gap-2">
                      <HelpCircle className="h-4 w-4" />
                      Q&A Section
                    </Label>
                    
                    {qnaEntries.length === 0 ? (
                      <p className="text-sm text-muted-foreground">
                        No Q&A entries yet. Click "Add Question" to create your first one.
                      </p>
                    ) : (
                      <div className="space-y-4">
                        {qnaEntries.map((entry, index) => (
                          <Card key={index} className="border-l-4 border-l-primary">
                            <CardContent className="pt-4">
                              <div className="space-y-3">
                                <div className="flex items-center justify-between">
                                  <Label className="text-sm font-medium">
                                    Question {index + 1}
                                  </Label>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => removeQnaEntry(index)}
                                    className="text-destructive hover:text-destructive hover:bg-destructive/10"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                                <Input
                                  placeholder="Enter your question here..."
                                  value={entry.question}
                                  onChange={(e) => updateQnaEntry(index, 'question', e.target.value)}
                                />
                                <div className="space-y-1">
                                  <Label className="text-sm">Answer</Label>
                                  <Textarea
                                    placeholder="Enter the answer here... (supports Markdown)"
                                    value={entry.answer}
                                    onChange={(e) => updateQnaEntry(index, 'answer', e.target.value)}
                                    rows={3}
                                  />
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    )}
                    
                    {/* Add Question Button - Now positioned below existing questions */}
                    <div className="flex justify-center pt-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={addQnaEntry}
                        className="flex items-center gap-2"
                      >
                        <Plus className="h-4 w-4" />
                        Add Question
                      </Button>
                    </div>
                  </div>

                  {/* Submit Button */}
                  <div className="flex justify-end gap-3 pt-4 border-t">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={closeForm}
                    >
                      Cancel
                    </Button>
                    <Button type="submit" disabled={creating}>
                      {creating ? (isEditing ? "Updating..." : "Creating...") : (isEditing ? "Update Article" : "Create Article")}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Current Featured Article */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold mb-4">Currently Featured</h2>
          {featuredArticle ? (
            <Card className="border-primary/50 bg-primary/5">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">{featuredArticle.title}</CardTitle>
                  <Badge variant="default" className="flex items-center gap-1">
                    <Star className="h-3 w-3" />
                    Featured
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground text-sm mb-3">
                  {featuredArticle.excerpt}
                </p>
                <div className="flex items-center gap-4 text-xs text-muted-foreground">
                  <Badge variant="outline">{featuredArticle.category}</Badge>
                  <span>{featuredArticle.read_time}</span>
                  <span>{formatDate(featuredArticle.published_at)}</span>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className="border-dashed">
              <CardContent className="pt-6">
                <div className="text-center py-8">
                  <StarOff className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">
                    No article is currently featured. Select one from the list below.
                  </p>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* All Articles */}
        <div>
          <h2 className="text-xl font-semibold mb-4">All Articles</h2>
          
          {loading ? (
            <div className="space-y-4">
              {[...Array(5)].map((_, i) => (
                <Card key={i}>
                  <CardContent className="pt-6">
                    <div className="space-y-3">
                      <div className="h-6 bg-muted rounded animate-pulse"></div>
                      <div className="h-4 bg-muted rounded animate-pulse"></div>
                      <div className="h-4 bg-muted rounded animate-pulse w-3/4"></div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : articles.length === 0 ? (
            <Card>
              <CardContent className="pt-6">
                <div className="text-center py-8">
                  <p className="text-muted-foreground">No articles found.</p>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {articles.map((article) => (
                <Card key={article.id} className={article.featured ? "border-primary/50 bg-primary/5" : ""}>
                  <CardContent className="pt-6">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="font-semibold">{article.title}</h3>
                          {article.featured && (
                            <Badge variant="default" className="flex items-center gap-1">
                              <Star className="h-3 w-3" />
                              Featured
                            </Badge>
                          )}
                        </div>
                        <p className="text-muted-foreground text-sm mb-3">
                          {article.excerpt}
                        </p>
                        <div className="flex items-center gap-4 text-xs text-muted-foreground">
                          <Badge variant="outline">{article.category}</Badge>
                          <span>{article.read_time}</span>
                          <span>{formatDate(article.published_at)}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant={article.featured ? "destructive" : "outline"}
                          size="sm"
                          onClick={() => toggleFeatured(article.id, article.featured)}
                          disabled={updatingId === article.id}
                          className="flex items-center gap-2"
                        >
                          {updatingId === article.id ? (
                            "Updating..."
                          ) : article.featured ? (
                            <>
                              <StarOff className="h-4 w-4" />
                              Remove Featured
                            </>
                          ) : (
                            <>
                              <Star className="h-4 w-4" />
                              Set as Featured
                            </>
                          )}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openEditForm(article)}
                          className="flex items-center gap-2"
                        >
                          <Edit className="h-4 w-4" />
                          Edit
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
        
        {/* Admin Activity Logs */}
        <div className="mt-8">
          <AuditLogsView />
        </div>
      </div>
    </div>
  );
};

export default Admin;